/**
 * RTWO Blockchain Protocol Engine — Repositories
 * Phase 3 | Version: 1.0 Protocol Edition
 *
 * In-memory stores remain empty until RPC/Ledger adapters ingest data.
 */

import type {
  ProtocolAddress,
  ProtocolHash,
  ProtocolNonce,
  RTWOAccount,
  RTWOBlock,
  RTWOReceipt,
  RTWOState,
  RTWOTransaction,
} from '../domain-models';
import { createEmptyMempool, createEmptyState } from '../domain-models';

class InMemoryMapRepository<T> {
  private readonly store = new Map<string, T>();

  constructor(private readonly getKey: (entity: T) => string) {}

  findAll(): T[] {
    return Array.from(this.store.values());
  }

  findById(id: string): T | null {
    return this.store.get(id) ?? null;
  }

  save(entity: T): void {
    this.store.set(this.getKey(entity), entity);
  }

  delete(id: string): boolean {
    return this.store.delete(id);
  }

  count(): number {
    return this.store.size;
  }
}

export class BlockRepository {
  private readonly byHash = new InMemoryMapRepository<RTWOBlock>(
    (block) => block.blockHash ?? `pending-${block.blockNumber}`
  );
  private readonly byNumber = new Map<string, RTWOBlock>();

  findAll(): RTWOBlock[] {
    return this.byHash
      .findAll()
      .sort((a, b) => Number(b.blockNumber ?? BigInt(0)) - Number(a.blockNumber ?? BigInt(0)));
  }

  findByHash(hash: ProtocolHash): RTWOBlock | null {
    return this.byHash.findById(hash);
  }

  findByNumber(blockNumber: bigint): RTWOBlock | null {
    return this.byNumber.get(blockNumber.toString()) ?? null;
  }

  findLatest(): RTWOBlock | null {
    return this.findAll()[0] ?? null;
  }

  save(block: RTWOBlock): boolean {
    if (!block.blockHash) {
      return false;
    }

    const existingByHash = this.byHash.findById(block.blockHash);
    if (existingByHash) {
      return false;
    }

    if (block.blockNumber !== null) {
      const existingByNumber = this.byNumber.get(block.blockNumber.toString());
      if (existingByNumber) {
        if (existingByNumber.blockHash === block.blockHash) {
          return false;
        }
        return false;
      }
    }

    this.byHash.save(block);
    if (block.blockNumber !== null) {
      this.byNumber.set(block.blockNumber.toString(), block);
    }

    return true;
  }

  hasHeightConflict(block: RTWOBlock): boolean {
    if (block.blockNumber === null || !block.blockHash) {
      return false;
    }

    const existing = this.byNumber.get(block.blockNumber.toString());
    return existing !== undefined && existing.blockHash !== block.blockHash;
  }

  count(): number {
    return this.byHash.count();
  }

  clearForTests(): void {
    for (const block of this.byHash.findAll()) {
      if (block.blockHash) {
        this.byHash.delete(block.blockHash);
      }
    }
    this.byNumber.clear();
  }
}

export class TransactionRepository {
  private readonly byHash = new InMemoryMapRepository<RTWOTransaction>(
    (tx) => tx.transactionHash ?? `pending-${tx.sender}-${tx.nonce}`
  );

  findAll(): RTWOTransaction[] {
    return this.byHash.findAll();
  }

  findByHash(hash: ProtocolHash): RTWOTransaction | null {
    return this.byHash.findById(hash);
  }

  save(transaction: RTWOTransaction): void {
    this.byHash.save(transaction);
  }

  count(): number {
    return this.byHash.count();
  }
}

export class AccountRepository {
  private readonly accounts = new InMemoryMapRepository<RTWOAccount>(
    (account) => account.address ?? 'unknown'
  );

  findAll(): RTWOAccount[] {
    return this.accounts.findAll();
  }

  findByAddress(address: ProtocolAddress): RTWOAccount | null {
    return this.accounts.findById(address);
  }

  save(account: RTWOAccount): void {
    this.accounts.save(account);
  }

  count(): number {
    return this.accounts.count();
  }
}

export class ReceiptRepository {
  private readonly receipts = new InMemoryMapRepository<RTWOReceipt>(
    (receipt) => receipt.transactionHash ?? 'unknown'
  );

  findByHash(hash: ProtocolHash): RTWOReceipt | null {
    return this.receipts.findById(hash);
  }

  save(receipt: RTWOReceipt): void {
    this.receipts.save(receipt);
  }

  findAll(): RTWOReceipt[] {
    return this.receipts.findAll();
  }
}

export class MempoolRepository {
  pending: RTWOTransaction[] = [];
  validationQueue: RTWOTransaction[] = [];
  readyQueue: RTWOTransaction[] = [];
  rejectedQueue: RTWOTransaction[] = [];

  reset(): void {
    const empty = createEmptyMempool();
    this.pending = empty.pendingTransactions;
    this.validationQueue = empty.validationQueue;
    this.readyQueue = empty.readyQueue;
    this.rejectedQueue = empty.rejectedQueue;
  }

  snapshot() {
    return {
      pendingTransactions: [...this.pending],
      queueSize:
        this.pending.length +
        this.validationQueue.length +
        this.readyQueue.length +
        this.rejectedQueue.length,
      validationQueue: [...this.validationQueue],
      readyQueue: [...this.readyQueue],
      rejectedQueue: [...this.rejectedQueue],
    };
  }

  findTransaction(hash: ProtocolHash): RTWOTransaction | null {
    const pools = [this.pending, this.validationQueue, this.readyQueue, this.rejectedQueue];
    for (const pool of pools) {
      const found = pool.find((tx) => tx.transactionHash === hash);
      if (found) {
        return found;
      }
    }
    return null;
  }

  replaceTransaction(hash: ProtocolHash, updated: RTWOTransaction): boolean {
    const pools = [
      { name: 'pending', list: this.pending },
      { name: 'validation', list: this.validationQueue },
      { name: 'ready', list: this.readyQueue },
      { name: 'rejected', list: this.rejectedQueue },
    ] as const;

    for (const pool of pools) {
      const index = pool.list.findIndex((tx) => tx.transactionHash === hash);
      if (index >= 0) {
        pool.list[index] = updated;
        return true;
      }
    }

    return false;
  }
}

export class StateRepository {
  private currentState: RTWOState = createEmptyState();

  getCurrentState(): RTWOState {
    return { ...this.currentState };
  }

  setCurrentState(state: RTWOState): void {
    this.currentState = { ...state };
  }
}

export class NonceReservationRepository {
  private readonly reserved = new Map<ProtocolAddress, Set<string>>();

  reserve(address: ProtocolAddress, nonce: ProtocolNonce): boolean {
    if (nonce === null) {
      return false;
    }

    const key = nonce.toString();
    const existing = this.reserved.get(address) ?? new Set<string>();
    if (existing.has(key)) {
      return false;
    }

    existing.add(key);
    this.reserved.set(address, existing);
    return true;
  }

  release(address: ProtocolAddress, nonce: ProtocolNonce): void {
    if (nonce === null) {
      return;
    }

    const existing = this.reserved.get(address);
    existing?.delete(nonce.toString());
  }

  isReserved(address: ProtocolAddress, nonce: ProtocolNonce): boolean {
    if (nonce === null) {
      return false;
    }
    return this.reserved.get(address)?.has(nonce.toString()) ?? false;
  }
}

export class ProtocolRepositories {
  readonly blocks = new BlockRepository();
  readonly transactions = new TransactionRepository();
  readonly accounts = new AccountRepository();
  readonly receipts = new ReceiptRepository();
  readonly mempool = new MempoolRepository();
  readonly state = new StateRepository();
  readonly nonceReservations = new NonceReservationRepository();
}

export function createProtocolRepositories(): ProtocolRepositories {
  return new ProtocolRepositories();
}

let sharedProtocolRepositories: ProtocolRepositories | null = null;

/** Shared repository set used by the protocol engine singleton and blockchain-core adapter. */
export function getProtocolRepositoriesSingleton(): ProtocolRepositories {
  if (!sharedProtocolRepositories) {
    sharedProtocolRepositories = createProtocolRepositories();
  }
  return sharedProtocolRepositories;
}

export function resetProtocolRepositoriesSingletonForTests(): void {
  sharedProtocolRepositories = null;
}
